import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { aboutUsData } from '../data/mockDataExt';
import { useSEO } from '../hooks/useSEO';

const AboutUs = () => {
    const { t } = useTranslation();
    useSEO({
        title: 'Nosotros | Laz Metals - Trayectoria Industrial Metalúrgica',
        description: 'Conoce nuestro ADN operativo. Somos el eslabón logístico y financiero crítico que conecta activos inactivos de chatarra con el complejo siderúrgico nacional.',
    });

    return (
        <div className="w-full">
            {/* CORPORATE MANIFESTO HERO */}
            <section className="min-h-[80dvh] lg:min-h-[60vh] py-16 lg:py-32 massive-container bg-bg-tertiary flex flex-col items-center justify-center relative overflow-hidden mt-6 text-white text-left md:text-center group">
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[20s] group-hover:scale-105"
                    style={{ backgroundImage: 'url(/images/about/banner-about-us-large.jpeg)' }}
                />
                <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at center, #7FB08E 0%, transparent 40%)' }} />

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="relative z-10 px-4 md:px-12 max-w-4xl"
                >
                    <div className="section-tag-orange mb-6 text-primary-light border-primary/30 md:mx-auto inline-flex">{t('aboutPage.heroTag')}</div>
                    <h1 className="text-3xl md:text-6xl font-heading font-medium mb-8 leading-tight text-white drop-shadow-md">
                        {t('aboutPage.heroTitle')}
                    </h1>
                    <p className="text-gray-300 font-body text-lg md:text-xl font-light leading-relaxed">
                        {t(aboutUsData.manifesto)}
                    </p>
                </motion.div>
            </section>

            {/* DUAL LAYOUT: THE MUSCLE */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="relative rounded-card overflow-hidden h-96 md:h-[500px] bg-gray-200 shadow-lg"
                        >
                            <img
                                src="/images/about/musculo-logistico-large.jpeg"
                                alt="Gandola y logística metalúrgica de Laz Metals"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 border-[8px] border-white/20 rounded-card" />
                            <div className="absolute bottom-6 left-6 p-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-xl font-heading font-bold text-accent">
                                {t('aboutPage.muscleTag')}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <h1 className="text-3xl lg:text-6xl font-heading font-medium text-accent mb-6 leading-tight">
                                {t('aboutPage.muscleTitle')}
                            </h1>
                            <div className="space-y-6 text-gray-600 font-body text-base leading-relaxed">
                                <p>
                                    {t('aboutPage.muscleP1')}
                                </p>
                                <div className="p-6 bg-bg-secondary border-l-4 border-primary rounded-r-card">
                                    <p className="italic font-medium text-gray-700">
                                        {t('aboutPage.muscleQuote')}
                                    </p>
                                </div>
                                <p>
                                    {t('aboutPage.muscleP2')}
                                </p>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* MISSION & VISION */}
            <section className="py-20 bg-bg-primary border-y border-gray-100">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-center md:text-left">
                        <div className="p-10 bg-white rounded-card shadow-sm border border-gray-100">
                            <h3 className="section-tag-green mb-4">{t('aboutPage.missionTag')}</h3>
                            <p className="text-gray-600 font-body leading-relaxed">{t(aboutUsData.mission)}</p>
                        </div>
                        <div className="p-10 bg-white rounded-card shadow-sm border border-gray-100 mt-0 md:mt-12">
                            <h3 className="section-tag-green mb-4">{t('aboutPage.visionTag')}</h3>
                            <p className="text-gray-600 font-body leading-relaxed">{t(aboutUsData.vision)}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS GRID */}
            <section className="py-24">
                <div className="container mx-auto px-6 max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-2xl md:text-4xl font-heading font-medium text-accent mb-4">{t('aboutPage.pillarsTitle')}</h2>
                        <p className="text-gray-500 font-body max-w-2xl mx-auto">{t('aboutPage.pillarsDesc')}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {aboutUsData.pillars.map((pillar, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: idx * 0.1 }}
                                className="bg-bg-secondary p-8 rounded-card border-t-4 border-primary hover:-translate-y-2 transition-transform duration-300"
                            >
                                <div className="text-5xl font-heading text-primary/20 font-bold mb-4">0{idx + 1}</div>
                                <h4 className="font-heading font-bold text-lg text-accent mb-4 leading-tight">{t(pillar.title)}</h4>
                                <p className="text-gray-500 font-body text-sm leading-relaxed">{t(pillar.description)}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default AboutUs;
